import React, { useState, useEffect } from 'react';
import { FiShare2, FiCopy, FiCheck, FiX, FiInfo, FiUpload, FiMail, FiPlus } from 'react-icons/fi'; 
import { SiGmail, SiMicrosoftoutlook } from "react-icons/si"; 
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx'; 
import './ShareLinkModal.css';
import { useToast } from '../../context/ToastContext/ToastContext';

const ShareLinkModal = ({ isOpen, onClose, onLinkCreated }) => {
    const { t } = useTranslation();
    const [linkName, setLinkName] = useState('');
    const [description, setDescription] = useState('');
    const [expiryDays, setExpiryDays] = useState(30);
    const [maxSubmissions, setMaxSubmissions] = useState('');
    const [generatedLink, setGeneratedLink] = useState('');
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [userInfo, setUserInfo] = useState(null);
    const [emailCheckEnabled, setEmailCheckEnabled] = useState(true);
    
    // Yetkili Mailler State'leri
    const [extractedEmails, setExtractedEmails] = useState([]);
    const [manualEmail, setManualEmail] = useState('');
    const [emailFileError, setEmailFileError] = useState('');
    const [showCopyNote, setShowCopyNote] = useState(false);
    useEffect(() => {
        if (isOpen) {
            fetchUserInfo();
        }
    }, [isOpen]);

    const fetchUserInfo = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch('/api/user/me', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setUserInfo(data.user);
            }
        } catch (error) {
            console.error(t('shareLinkModal.userInfoError'), error);
        }
    };
    
    const hasUnlimitedPackage = userInfo?.unlimitedPackageExpiresAt && 
        new Date(userInfo.unlimitedPackageExpiresAt) > new Date();
    
    const availableTokens = userInfo?.token || 0;

    // --- MANUEL E-POSTA EKLEME ---
    const handleAddManualEmail = (e) => {
        e.preventDefault();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (!manualEmail) return;

        if (emailRegex.test(manualEmail.trim())) {
            const newEmail = manualEmail.trim().toLowerCase();
            if (!extractedEmails.includes(newEmail)) {
                setExtractedEmails([...extractedEmails, newEmail]);
            }
            setManualEmail('');
            setEmailFileError('');
        } else {
            setEmailFileError(t('shareLinkModal.invalidEmail') || 'Lütfen geçerli bir e-posta adresi giriniz.');
        }
    };

    // --- E-POSTA SİLME ---
    const handleRemoveEmail = (emailToRemove) => {
        setExtractedEmails(extractedEmails.filter(email => email !== emailToRemove));
    };

    // --- EXCEL OKUMA FONKSİYONU ---
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: 'binary' });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
            
            let emails = [];
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            data.forEach(row => {
                row.forEach(cell => {
                    if (typeof cell === 'string' && emailRegex.test(cell.trim())) {
                        emails.push(cell.trim().toLowerCase());
                    }
                });
            });

            if (emails.length > 0) {
                // Mevcut maillerle birleştir ve tekrar edenleri sil
                const combinedEmails = [...extractedEmails, ...emails];
                const uniqueEmails = [...new Set(combinedEmails)];
                setExtractedEmails(uniqueEmails);
                setEmailFileError('');
            } else {
                setEmailFileError(t('shareLinkModal.noEmailsFound') || 'Dosyada e-posta bulunamadı.');
            }
        };
        reader.readAsBinaryString(file);
        e.target.value = ''; // Aynı dosyayı tekrar yükleyebilmek için inputu temizle
    };

    // --- MAIL GÖNDERME BUTONLARI İÇİN FONKSİYONLAR ---
 
const copyEmailsAndNotify = (separator = '; ') => {
    if (!extractedEmails || extractedEmails.length === 0) return;
    
    const emailString = extractedEmails.join(separator);
    navigator.clipboard.writeText(emailString).then(() => {
        setShowCopyNote(true);
        // Notun 6 saniye ekranda kalmasını sağlayalım (kullanıcı mail ekranına geçtiğinde bile görsün)
        setTimeout(() => setShowCopyNote(false), 10000); 
    }).catch(err => {
        console.error('Kopyalama hatası:', err);
    });
};

const sendViaGmail = () => {
    if (extractedEmails.length === 0) return;
    copyEmailsAndNotify(', '); // Gmail virgül sever
    const subject = encodeURIComponent(t('shareLinkModal.invitationSubject') || 'Değerleme Raporu Daveti');
    const body = encodeURIComponent(`${t('shareLinkModal.invitationBody') || 'Merhaba,'}\n\n${generatedLink}\n\n`);
    const bcc = extractedEmails.join(',');
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&bcc=${bcc}&su=${subject}&body=${body}`;
    window.open(gmailUrl, '_blank');
};

const sendViaOutlookWeb = () => {
    if (extractedEmails.length === 0) return;
    
    copyEmailsAndNotify('; ');
    
    setTimeout(() => {
        const subject = encodeURIComponent(t('shareLinkModal.invitationSubject') || 'Değerleme Raporu Daveti');
        const body = encodeURIComponent(`${t('shareLinkModal.invitationBody') || 'Merhaba,'}\n\n${generatedLink}\n\n`);
        const bcc = extractedEmails.join(';');
        const outlookUrl = `https://outlook.office.com/mail/deeplink/compose?bcc=${bcc}&subject=${subject}&body=${body}`;
        window.open(outlookUrl, '_blank');
    }, 1500);
};

const sendViaOutlookDesktop = () => {
    if (extractedEmails.length === 0) return;
    
    copyEmailsAndNotify(', '); 
    
    setTimeout(() => {
        const subject = encodeURIComponent(t('shareLinkModal.invitationSubject') || 'Değerleme Raporu Daveti');
        const body = encodeURIComponent(`${t('shareLinkModal.invitationBody') || 'Merhaba,'}\n\n${generatedLink}\n\n`);
        const bcc = extractedEmails.join(',');
        window.location.href = `mailto:?bcc=${bcc}&subject=${subject}&body=${body}`;
    }, 1500);
};
    const copyEmailsToClipboard = () => {
        navigator.clipboard.writeText(extractedEmails.join('; '));
        alert(t('shareLinkModal.emailsCopied') || 'E-postalar kopyalandı!');
    };

    const handleGenerateLink = async () => {
    setError('');
    
    if (!linkName.trim()) {
        setError(t('shareLinkModal.linkNameRequired'));
        return;
    }

    if (emailCheckEnabled && extractedEmails.length === 0) {
        setError(
            t('shareLinkModal.emailsRequired') ||
            'Lütfen linki oluşturmadan önce en az bir e-posta adresi ekleyin veya Excel yükleyin.'
        );
        return;
    }

    if (!hasUnlimitedPackage) {
        if (!maxSubmissions || maxSubmissions === '') {
            setError(t('shareLinkModal.maxSubmissionsRequired'));
            return;
        }
        
        const submissionCount = parseInt(maxSubmissions);
        if (submissionCount > availableTokens) {
            setError(t('shareLinkModal.maxSubmissionsError', { available: availableTokens }));
            return;
        }

        if (submissionCount < 1) {
            setError(t('shareLinkModal.minSubmissionsError'));
            return;
        }
    }

    if (
        emailCheckEnabled &&
        maxSubmissions &&
        extractedEmails.length > parseInt(maxSubmissions)
    ) {
        setError(
            t('shareLinkModal.tooManyEmailsError') ||
            `Belirlediğiniz gönderim limitinden (${maxSubmissions}) daha fazla e-posta eklediniz.`
        );
        return;
    }

    setLoading(true);

    try {
        const token = localStorage.getItem('userToken');
        const response = await fetch('/api/shared-links/create', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                linkName: linkName.trim(),
                description: description.trim(),
                expiryDays,
                maxSubmissions: maxSubmissions ? parseInt(maxSubmissions) : null,
                emailCheckEnabled,
                allowedEmails: emailCheckEnabled ? extractedEmails : []
            })
        });

        const data = await response.json();

        if (response.ok) {
            setGeneratedLink(data.link);
            if (onLinkCreated) onLinkCreated();
        } else {
            setError(data.msg || t('shareLinkModal.errorCreatingLink'));
        }
    } catch (err) {
        setError(t('shareLinkModal.connectionError'));
        console.error(err);
    } finally {
        setLoading(false);
    }
};

    const handleCopyLink = () => {
        navigator.clipboard.writeText(generatedLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleClose = () => {
    setLinkName('');
    setDescription('');
    setExpiryDays(30);
    setMaxSubmissions('');
    setGeneratedLink('');
    setCopied(false);
    setError('');
    setExtractedEmails([]);
    setManualEmail('');
    setEmailFileError('');
    setEmailCheckEnabled(true);
    onClose();
};

    if (!isOpen) return null;

    return (
        <div className="share-modal-overlay" onClick={handleClose}>
            <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="share-modal-header">
                    <div className="share-modal-title">
                        <FiShare2 size={24} />
                        <h2>{t('shareLinkModal.title')}</h2>
                    </div>
                    <button className="share-modal-close" onClick={handleClose}>
                        <FiX size={24} />
                    </button>
                </div>

                <div className="share-modal-body">
                    {!generatedLink ? (
                        <>
                            
                            <div className="share-form-group">
                                <label>{t('shareLinkModal.linkName')} *</label>
                                <input
                                    type="text"
                                    value={linkName}
                                    onChange={(e) => setLinkName(e.target.value)}
                                    placeholder={t('shareLinkModal.linkNamePlaceholder')}
                                    required
                                />
                            </div>

                            <div className="share-form-group">
                                <label>{t('shareLinkModal.description')}</label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder={t('shareLinkModal.descriptionPlaceholder')}
                                    rows="3"
                                />
                            </div>

                            <div className="share-form-group">
                          

                            <div className="share-form-group">
                            <label>{t('shareLinkModal.linkType') || 'Link Type'}</label>

                            <div className="link-type-toggle">
                                <button
                                    type="button"
                                    className={`toggle-btn ${emailCheckEnabled ? 'active' : ''}`}
                                    onClick={() => setEmailCheckEnabled(true)}
                                >
                                    {t('shareLinkModal.emailControlledLink') || 'Email controlled'}
                                </button>

                                <button
                                    type="button"
                                    className={`toggle-btn ${!emailCheckEnabled ? 'active' : ''}`}
                                    onClick={() => {
                                        setEmailCheckEnabled(false);
                                        setExtractedEmails([]);
                                        setManualEmail('');
                                        setEmailFileError('');
                                    }}
                                >
                                    {t('shareLinkModal.openLink') || 'Open link'}
                                </button>
                            </div>
                        </div>
                        </div>

                            <div className="share-form-row">
                                <div className="share-form-group">
                                    <label>{t('shareLinkModal.linkValidity')}</label>
                                    <select
                                        value={expiryDays}
                                        onChange={(e) => setExpiryDays(Number(e.target.value))}
                                    >
                                        <option value={7}>{t('shareLinkModal.days7')}</option>
                                        <option value={14}>{t('shareLinkModal.days14')}</option>
                                        <option value={30}>{t('shareLinkModal.days30')}</option>
                                        <option value={60}>{t('shareLinkModal.days60')}</option>
                                        <option value={90}>{t('shareLinkModal.days90')}</option>
                                    </select>
                                </div>

                                <div className="share-form-group">
                                    <label>
                                        {t('shareLinkModal.maxSubmissions')} 
                                        {!hasUnlimitedPackage && <span style={{ color: '#c62828' }}> *</span>}
                                    </label>
                                    <input
                                        type="number"
                                        value={maxSubmissions}
                                        onChange={(e) => setMaxSubmissions(e.target.value)}
                                        placeholder={hasUnlimitedPackage 
                                            ? t('shareLinkModal.maxSubmissionsPlaceholderUnlimited')
                                            : t('shareLinkModal.maxSubmissionsPlaceholder')
                                        }
                                        min="1"
                                        max={hasUnlimitedPackage ? undefined : availableTokens}
                                        disabled={!userInfo} 
                                    />
                                    <small>
                                        {hasUnlimitedPackage 
                                            ? t('shareLinkModal.unlimitedPackageInfo')
                                            : t('shareLinkModal.availableTokens', { tokens: availableTokens })
                                        }
                                    </small>
                                </div>
                            </div>
                                      
                            {/* YENİ: YETKİLİ E-POSTALAR BÖLÜMÜ */}
                            {emailCheckEnabled && (
    <div className="email-share-section" style={{ marginBottom: '24px' }}>
                                <h4>
                                    <FiMail style={{marginRight: '8px', verticalAlign:'middle'}}/>
                                    {t('shareLinkModal.authorizedEmails') || 'Yetkili E-postalar'} <span style={{ color: '#c62828' }}>*</span>
                                </h4>
                                  <div className="share-info-banner">
                                <FiInfo size={20} />
                                <p>{t('shareLinkModal.authorizedEmailsDesc')}</p>
                            </div>
                            
                                
                                
                                <div className="manual-email-input-group">
                                    <input 
                                        type="email" 
                                        placeholder={t('shareLinkModal.emailPlaceholder')}
                                        value={manualEmail}
                                        onChange={(e) => setManualEmail(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddManualEmail(e)}
                                    />
                                    <button type="button" className="add-email-btn" onClick={handleAddManualEmail}>
                                       <FiPlus /> {t('shareLinkModal.addButton')}
                                    </button>
                                </div>

                                <div className="divider">{t('shareLinkModal.orExtractFromExcel')}</div>

                                <div className="file-upload-wrapper">
                                    <label htmlFor="excel-upload" className="file-upload-label">
                                        <FiUpload /> {t('shareLinkModal.uploadExcel') || 'Excel Listesi Yükle (.xlsx)'}
                                    </label>
                                    <input 
                                        type="file" 
                                        id="excel-upload" 
                                        accept=".xlsx, .xls" 
                                        onChange={handleFileUpload} 
                                        className="file-upload-input"
                                    />
                                </div>

                                {emailFileError && <div className="share-error-message" style={{fontSize: '13px', marginTop: '10px'}}>{emailFileError}</div>}

                                {extractedEmails.length > 0 && (
                                    <div className="email-chips-container">
                                        {extractedEmails.map((email, idx) => (
                                            <span key={idx} className="email-chip">
                                                {email}
                                                <button type="button" className="email-chip-remove" onClick={() => handleRemoveEmail(email)}>
                                                    <FiX size={14} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                               </div>
                                )}
                           

                            {error && (
                                <div className="share-error-message">
                                    {error}
                                </div>
                            )}

                            <button
                                className="share-generate-btn"
                                onClick={handleGenerateLink}
                                disabled={loading}
                            >
                                {loading ? t('shareLinkModal.generating') : t('shareLinkModal.createLink')}
                            </button>
                        </>
                    ) : (
                        /* --- LINK OLUŞTUKTAN SONRA KISMI --- */
                        <>
                            <div className="share-success-message">
                                {t('shareLinkModal.successMessage')}
                            </div>

                            <div className="share-link-container">
                                <input
                                    type="text"
                                    value={generatedLink}
                                    readOnly
                                    className="share-link-input"
                                />
                                <button
                                    className="share-copy-btn"
                                    onClick={handleCopyLink}
                                >
                                    {copied ? <FiCheck size={20} /> : <FiCopy size={20} />}
                                    {copied ? t('shareLinkModal.copied') : t('shareLinkModal.copyLink')}
                                </button>
                            </div>

                            {/* OLUŞTURMADAN ÖNCE EKLENEN MAILLERE DAVET GÖNDERME KISMI */}
                            {extractedEmails.length > 0 && (
                                <div className="email-share-section">
                                    <h4>
                                        <FiCheck color="green" style={{marginRight: '8px'}} /> 
                                        {extractedEmails.length} {t('shareLinkModal.emailFound') || 'yetkili e-posta eklendi.'}
                                    </h4>
                                    <p style={{fontSize: '13px', color: '#666', marginBottom: '12px'}}>
                                        {t('shareLinkModal.inviteNowDesc') || 'Aşağıdaki butonları kullanarak yetki verdiğiniz kişilere linki topluca gönderebilirsiniz.'}
                                    </p>

                                    {showCopyNote && (
                                <div style={{
                                    backgroundColor: '#fff4e5', // Dikkat çekici açık turuncu/sarı
                                    color: '#663c00',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    marginBottom: '16px',
                                    border: '1px solid #ffe2b7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    animation: 'fadeIn 0.3s ease-in'
                                }}>
                                    <FiInfo size={20} color="#ff9800" />
                                    <div>
                                        <strong>E-postalar kopyalandı!</strong><br />
                                        Outlook'ta alıcı listesi boş görünürse <strong>BCC alanına yapıştır (CTRL+V)</strong> yapabilirsiniz.
                                    </div>
                                </div>
                            )}
                                    
                                  <div className="mail-buttons-grid">
                                    <button onClick={sendViaGmail} className="mail-btn gmail-btn">
                                        <SiGmail /> Gmail
                                    </button>
                                    <button onClick={sendViaOutlookWeb} className="mail-btn outlook-web-btn">
                                        <SiMicrosoftoutlook /> Outlook Web
                                    </button>
                                    {/* YENİ: Masaüstü Butonu */}
                                    <button onClick={sendViaOutlookDesktop} className="mail-btn desktop-mail-btn">
                                        <FiMail /> Masaüstü App
                                    </button>
                                    
                                    {/* Izgara yapını korumak için span değerini CSS'ine göre ayarlayabilirsin (örn: span 3 veya span 4) */}
                                    <button onClick={copyEmailsToClipboard} className="mail-btn copy-mails-btn" style={{gridColumn: '1 / -1'}}>
                                        <FiCopy /> {t('shareLinkModal.copyEmails')}
                                    </button>
                                </div>
                                    <small style={{display:'block', marginTop:'8px', color:'#666', fontSize:'11px', textAlign:'center'}}>
                                        * Gizlilik için alıcılar BCC alanına eklenir.
                                    </small>
                                </div>
                            )}

                            <button
                            className="share-new-link-btn"
                            onClick={() => {
                                setGeneratedLink('');
                                setLinkName('');
                                setDescription('');
                                setMaxSubmissions('');
                                setError('');
                                setExtractedEmails([]);
                                setManualEmail('');
                                setEmailCheckEnabled(true);
                            }}
                        >
                            {t('shareLinkModal.createNewLink')}
                        </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShareLinkModal;